import React, { useState } from 'react'
import { Box, Button, Typography, useTheme } from '@mui/material'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import PhotovoltaicTabs from './PhotovoltaicTabs'
import { FormStepProps } from 'src/Libs/installation-statement/components/types'
import { FieldArray, Form, Formik, FormikProps } from 'formik'
import { usePhotovoltaicValidation } from 'src/Libs/installation-statement/hooks/validation'
import PhotovoltaicForm from 'src/Libs/installation-statement/components/Photovoltaic/PhotovoltaicForm'
import { makeStyles } from 'tss-react/mui'
import { DEFAULT_PHOTOVOLTAIC_TRACKER_DATA, PhotovoltaicFormData } from 'src/Libs/installation-statement/configurations/photovoltaic'
import { getThemedBlankViewImage } from 'src/Libs/utils'
import { BlankViewImageVariants } from 'src/Components/blank-view'
import { MAXIMUM_NUMBER_OF_TRACKERS } from './photovoltaic.constants'

const useStyles = makeStyles()(theme => ({
  container: {
    position: 'relative',
    padding: 0,
    [theme.breakpoints.down('md')]: {
      padding: 0,
    },
  },
  noTrackerBox: {
    backgroundColor: theme.palette.grey.grey07,
    borderRadius: theme.shape.borderRadius,
    marginTop: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
  },
  noTrackerImage: {
    maxWidth: '120px',
    margin: `${theme.spacing(4)} auto ${theme.spacing(2)}`,
  },
  addPvTrackerButton: {
    width: 'fit-content',
    margin: `${theme.spacing(2)} auto ${theme.spacing(4)}`,
  },
  centeredTypography: {
    width: 'fit-content',
    margin: 'auto',
    textAlign: 'center',
  },
}))

/**
 * Component represents the installation statement wizard step "photovoltaic"
 * Step consists of 1-n tabs which represent a photovoltaic tracker each
 */
const Photovoltaic: React.FC<FormStepProps<PhotovoltaicFormData>> = ({ data, setFormData, setFinalValidation, setTouched }) => {
  const { classes } = useStyles()
  const [activeTab, setActiveTab] = useState(0)
  const { inProgressValidationSchema, finalValidationSchema } = usePhotovoltaicValidation()
  const blankViewImageNeutral = getThemedBlankViewImage(BlankViewImageVariants.Neutral)

  const { t } = useCustomTranslation()
  const theme = useTheme()

  const handleValidationForFinalValidationSchema = async (values: PhotovoltaicFormData): Promise<void> => {
    const response = await finalValidationSchema.isValid(values)
    setFinalValidation(response)
  }

  const addTracker = (push: (obj: unknown) => void): void => {
    if (data.pvTrackers.length >= MAXIMUM_NUMBER_OF_TRACKERS) {
      return
    }
    push(DEFAULT_PHOTOVOLTAIC_TRACKER_DATA())
    if (activeTab === -1) {
      setActiveTab(0)
    }
  }

  const removeTracker = (remove: (index: number) => void, formData: FormikProps<PhotovoltaicFormData>): void => {
    const lastTrackerIndex = formData.values.pvTrackers.length - 1
    if (activeTab === lastTrackerIndex) setActiveTab(activeTab => activeTab - 1)

    remove(activeTab)
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      <Typography variant="h4" sx={{ marginBottom: theme.spacing(2) }}>
        {t('installation-statement.photovoltaic.title')}
      </Typography>
      <Formik
        initialValues={data}
        validationSchema={inProgressValidationSchema}
        onSubmit={() => {
          /* */
        }}
        validateOnMount
      >
        {formData => {
          if (formData.dirty) {
            setTouched(true)
          }

          setFormData(formData)
          handleValidationForFinalValidationSchema(formData.values)

          return (
            <Form
            // placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}
            >
              <FieldArray
                name={'pvTrackers'}
                render={({ push, remove }) => {
                  const handleRemoveTracker = (): void => removeTracker(remove, formData)

                  return (
                    <>
                      <PhotovoltaicTabs
                        activeTab={activeTab}
                        tabs={data.pvTrackers}
                        addTracker={() => addTracker(push)}
                        setActiveTab={setActiveTab}
                      />
                      <Box>
                        {formData.values.pvTrackers.map((tracker, index) => (
                          <Box className={classes.container} hidden={index !== activeTab} key={index}>
                            <Button variant="text" onClick={handleRemoveTracker} sx={{ position: 'absolute', top: 2, right: 0, fontSize: 11 }}>
                              {t('installation-statement.photovoltaic.tab.remove').toUpperCase()}
                            </Button>
                            <PhotovoltaicForm trackerIndex={index} tracker={tracker} push={push} />
                          </Box>
                        ))}
                        {formData.values.pvTrackers.length === 0 && (
                          <Box className={classes.noTrackerBox}>
                            <Box className={classes.noTrackerImage} component="img" src={blankViewImageNeutral} />
                            <Typography className={classes.centeredTypography} sx={{ pb: 1 }} variant="body1">
                              {t('installation-statement.photovoltaic.tab.all-tracker-removed')}
                            </Typography>
                            <Typography className={classes.centeredTypography} variant="caption">
                              {t('installation-statement.photovoltaic.tab.add-new-tracker')}
                            </Typography>

                            <Button className={classes.addPvTrackerButton} variant="contained" onClick={() => addTracker(push)}>
                              {t('installation-statement.photovoltaic.tab.add-pv-tracker')}
                            </Button>
                          </Box>
                        )}
                      </Box>
                    </>
                  )
                }}
              />
            </Form>
          )
        }}
      </Formik>
    </Box>
  )
}

export default Photovoltaic
